import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { detectLiteCapability } from "./capabilities";
import { selectWorldMode } from "./tierPolicy";
import type {
  RendererDiagnostics,
  RendererHealth,
  WorldExperience,
  WorldMode,
  WorldProviderProps,
} from "./types";
const STORAGE_KEY = "dgc.world.preference.v1";
const SAFE_PREFERENCES: readonly WorldMode[] = [
  "off",
  "static",
  "lite",
  "balanced",
  "cinematic",
];
const WorldContext = createContext<WorldExperience | null>(null);
declare global {
  interface Window {
    __DGC_WORLD_DIAGNOSTICS__?: RendererDiagnostics;
  }
}
function initialPreference(): WorldMode {
  const override = new URLSearchParams(location.search).get("world");
  if (override === "off" || override === "static" || override === "lite")
    return override;
  const stored = localStorage.getItem(STORAGE_KEY);
  const safe = SAFE_PREFERENCES.includes(stored as WorldMode)
    ? (stored as WorldMode)
    : "static";
  return safe === "balanced" || safe === "cinematic" ? "static" : safe;
}
export function WorldExperienceProvider({ children }: WorldProviderProps) {
  const [preference, setPreferenceState] =
    useState<WorldMode>(initialPreference);
  const [rendererHealth, setRendererHealth] = useState<RendererHealth>("idle");
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);
  const [announcement, setAnnouncement] = useState("");
  const [capability] = useState(detectLiteCapability);
  const diagnostics = useRef<RendererDiagnostics>({
    selectedMode: "static",
    health: "idle",
    frameState: "idle",
    renderer: "unavailable",
    glbRequests: 0,
    decoderRequests: 0,
    recoveryCount: 0,
  });
  const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const dataSaver = Boolean(
    (navigator as Navigator & { connection?: { saveData?: boolean } })
      .connection?.saveData,
  );
  const requested = capability.allowed
    ? preference
    : preference === "off"
      ? "off"
      : "static";
  const mode = selectWorldMode({
    preference: requested,
    reducedMotion,
    dataSaver,
    balancedCapable: Boolean(capability.balancedAllowed),
    cinematicOptIn: false,
  });
  const publish = useCallback(() => {
    if (import.meta.env.DEV || navigator.webdriver)
      window.__DGC_WORLD_DIAGNOSTICS__ = { ...diagnostics.current };
  }, []);
  const setPreference = useCallback(
    (next: WorldMode) => {
      if (next === "lite" && !capability.allowed) return;
      if (next === "balanced" && !capability.balancedAllowed) return;
      setPreferenceState(next);
      localStorage.setItem(STORAGE_KEY, next);
      setAnnouncement(`World view: ${next}.`);
      setRecoveryAttempts(0);
      setRendererHealth(
        next === "lite" || next === "balanced" ? "idle" : "ready",
      );
    },
    [capability.allowed, capability.balancedAllowed],
  );
  const reportRendererFailure = useCallback(
    () =>
      setRecoveryAttempts((value) => {
        if (value < 2) {
          setRendererHealth("recovering");
          return value + 1;
        }
        setRendererHealth("failed");
        setPreferenceState("static");
        localStorage.setItem(STORAGE_KEY, "static");
        setAnnouncement("Playable world unavailable. Static view restored.");
        return value;
      }),
    [],
  );
  const reportRendererReady = useCallback(
    (renderer: string) => {
      setRendererHealth("ready");
      diagnostics.current.renderer = renderer;
      publish();
    },
    [publish],
  );
  const updateDiagnostics = useCallback(
    (update: {
      frameState?: RendererDiagnostics["frameState"];
      assetRequest?: string;
      effectiveRouteMode?: "off" | "static" | "lite" | "balanced";
      activeDistrictId?: string;
      activeSceneId?: string;
      visualPackId?: string;
      visualAssetId?: string;
      staticImageFailed?: boolean;
    }) => {
      if (update.frameState) diagnostics.current.frameState = update.frameState;
      if (update.assetRequest?.endsWith(".glb"))
        diagnostics.current.glbRequests++;
      if (update.assetRequest?.includes("/decoders/draco/"))
        diagnostics.current.decoderRequests++;
      if (update.effectiveRouteMode)
        diagnostics.current.effectiveRouteMode = update.effectiveRouteMode;
      if (update.activeDistrictId)
        diagnostics.current.activeDistrictId = update.activeDistrictId;
      if (update.activeSceneId)
        diagnostics.current.activeSceneId = update.activeSceneId;
      if (update.visualPackId)
        diagnostics.current.visualPackId = update.visualPackId;
      if (update.visualAssetId)
        diagnostics.current.visualAssetId = update.visualAssetId;
      if (update.staticImageFailed !== undefined)
        diagnostics.current.staticImageFailed = update.staticImageFailed;
      publish();
    },
    [publish],
  );
  useEffect(() => {
    if (mode !== "lite" && mode !== "balanced")
      setRendererHealth(mode === "static" ? "ready" : "idle");
    Object.assign(diagnostics.current, {
      selectedMode: mode,
      health: rendererHealth,
      recoveryCount: recoveryAttempts,
    });
    publish();
  }, [mode, rendererHealth, recoveryAttempts, publish]);
  const value = useMemo(
    () => ({
      mode,
      preference,
      rendererHealth,
      recoveryAttempts,
      capability,
      setPreference,
      reportRendererFailure,
      reportRendererReady,
      updateDiagnostics,
      announceMode: setAnnouncement,
    }),
    [
      mode,
      preference,
      rendererHealth,
      recoveryAttempts,
      capability,
      setPreference,
      reportRendererFailure,
      reportRendererReady,
      updateDiagnostics,
    ],
  );
  return (
    <WorldContext.Provider value={value}>
      {children}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </div>
      <div className="sr-only" aria-live="off">
        World renderer {rendererHealth}.
      </div>
    </WorldContext.Provider>
  );
}
export function useWorldExperience() {
  const value = useContext(WorldContext);
  if (!value)
    throw new Error(
      "useWorldExperience must be used within WorldExperienceProvider",
    );
  return value;
}
